import { useState } from "react"
import { Trans } from "@lingui/react/macro"
import { usePage } from "@inertiajs/react"
import type {
  DealDrawer as DealDrawerData,
  OwnerOption,
} from "@/types/v2/conversation"
import {
  pipelineLabelColor,
  pipelineSegmentColor,
} from "@/lib/deal-stage"
import { ActivityFeed } from "@/components/v2/deals/activity-feed"
import { DealPropertiesCard } from "@/components/v2/deals/deal-properties-card"
import { DealContactCard } from "@/components/v2/deals/deal-contact-card"
import { DealConversationsCard } from "@/components/v2/deals/deal-conversations-card"
import { AiPausedBanner } from "@/components/v2/deals/ai-paused-banner"
import { NextStepsSection } from "@/components/v2/deals/next-steps-section"
import type { NoteComposerEmployee } from "@/components/v2/deals/note-composer"
import {
  BASE_RELOAD_PROPS,
  type DrawerNavContext,
} from "@/lib/drawer-trail"
import { DrawerNavContextValue } from "@/components/v2/drawer/drawer-nav-context"
import {
  currentActivitySearch,
  reloadActivityOnly,
  runActivitySearch,
} from "@/lib/activity-search"
import type { SharedAuthProps } from "@/types/v2/auth"

interface DealEntryBodyProps {
  deal: DealDrawerData
  ownerOptions?: OwnerOption[]
  // Drawer trail nav context so the contact card can push
  // its contact onto the trail when the user clicks the
  // contact name.
  navContext: DrawerNavContext
}

// Body content for a deal entry in the unified v2
// slideover. Extracted from the legacy `DealDrawer` so
// the chrome lives in the unified Drawer and entries are
// content-only.
//
// CON-113 lifted the deal slideover from "read-only" to
// match what the full deal show page lets you do: the
// Next Steps section now wires through `currentEmployee`
// (so "+ Add next step" is enabled), and the shared
// `ActivityFeed` grows an "+ Add note" affordance + inline
// `NoteComposer` whenever it's handed a `deal` +
// `currentEmployee` (CON-145). The wire shape is identical
// to the show page — same `/v2/deals/:id/notes` endpoint,
// same broadcasts, same partial-reload pattern — so the
// drawer and the page stay reconcilable when both are
// open on the same deal in different tabs.
//
// `currentEmployee` is pulled from the shared `auth` prop
// (V2::BaseController) rather than threaded through
// `<Drawer>` from each host page — every host already has
// the auth share, so we'd just be moving the same data
// through one more layer.
export function DealEntryBody({
  deal,
  ownerOptions,
  navContext,
}: DealEntryBodyProps) {
  // Preview mode (CON-183): the body is rendered inside the
  // command-palette rich-preview pane, which supplies
  // `navContext.preview`. In that mode the inline-edit hooks
  // patch the pane's local state via the `"preview"` propKey,
  // and the read-only "+ Add note" / "+ Add next step" /
  // activity-search affordances route through the pane (or
  // are hidden) — see below.
  const previewMode = Boolean(navContext.preview)
  const propKey = previewMode ? "preview" : "drawer"

  const page = usePage<SharedAuthProps>()
  const authEmployee = page.props.auth?.employee ?? null
  // In preview mode we deliberately drop the current employee
  // so the note / next-step composers (which gate on it) stay
  // hidden — the palette preview is read-first; full editing
  // happens on the entity's own page / slideover.
  const currentEmployee: NoteComposerEmployee | null =
    !previewMode && authEmployee
      ? {
          id: authEmployee.id,
          name: authEmployee.name,
          initials: authEmployee.initials,
        }
      : null

  // Single-scroll drawer body. Hero, properties, AI
  // banner, next-steps, and the history feed all flow
  // naturally in one scroll container. The "+ Add note"
  // affordance + inline `NoteComposer` now live inside
  // `ActivityFeed` (CON-145) — gated on `deal` +
  // `currentEmployee`, both passed below. The activity
  // feed's shared scroll memory (chip switches keep the
  // scroll in place) is wired off this container's
  // scrollTop — captured into a `setScrollContainer`
  // callback ref (`useState<HTMLElement | null>`) and
  // passed to `ActivityFeed`.
  const [scrollContainer, setScrollContainer] =
    useState<HTMLElement | null>(null)
  return (
    <DrawerNavContextValue.Provider value={navContext}>
    <div
      ref={setScrollContainer}
      data-testid="drawer-body"
      className="flex-1 min-h-0 overflow-y-auto px-6 pt-6 pb-8 flex flex-col gap-6"
    >
      <DrawerHero deal={deal} />
      <StageStrip deal={deal} />
      {deal.aiPause ? (
        <AiPausedBanner aiPause={deal.aiPause} deal={{ id: deal.id }} />
      ) : null}
      <NextStepsSection
        deal={{ id: deal.id, name: deal.name }}
        pendingNextSteps={deal.pendingNextSteps}
        idleLabel={deal.idleLabel ?? null}
        dealStatus={deal.status}
        currentEmployee={currentEmployee}
        ownerOptions={ownerOptions ?? []}
      />
      <DealPropertiesCard deal={deal} dealPropKey={propKey} />
      <DealContactCard
        deal={deal}
        dealPropKey={propKey}
        navContext={navContext}
      />
      <DealConversationsCard deal={deal} dealPropKey={propKey} />
      <div
        data-testid="drawer-activity"
        className="flex flex-col gap-3 pt-3 border-t border-[#F0EDE6]"
      >
        <ActivityFeed
          items={deal.activity}
          variant="drawer"
          heading={<SectionLabel><Trans>History</Trans></SectionLabel>}
          deal={{ id: deal.id, name: deal.name }}
          currentEmployee={currentEmployee}
          composerVariant="inline"
          scrollContainer={scrollContainer}
          facets
          // Preview mode is read-only (no reload mechanism +
          // no composer), so the kebab stays off there. Live
          // slideover reloads the drawer's deferred activity
          // props — same set `runActivitySearch` reloads.
          onNoteDeleted={
            previewMode
              ? undefined
              : () =>
                  reloadActivityOnly([
                    ...BASE_RELOAD_PROPS,
                    ...(navContext.reloadProps ?? []),
                  ])
          }
          search={{
            initialValue: currentActivitySearch(),
            onSearch: previewMode
              ? (value) => navContext.preview!.searchActivity(value)
              : (value) =>
                  runActivitySearch(
                    navContext.path,
                    [
                      ...BASE_RELOAD_PROPS,
                      ...(navContext.reloadProps ?? []),
                    ],
                    value,
                  ),
          }}
        />
      </div>
    </div>
    </DrawerNavContextValue.Provider>
  )
}

function DrawerHero({ deal }: { deal: DealDrawerData }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="flex items-center justify-center shrink-0 rounded-lg bg-[#1E1B17] size-9">
        <div className="font-semibold text-[#F0EDE6] text-sm leading-[18px]">
          {deal.company.initial}
        </div>
      </div>
      <div className="flex flex-col grow min-w-0 gap-0.5">
        <div className="text-[#7A746B] text-[13px] leading-4 truncate">
          {deal.company.name}
        </div>
        <div className="tracking-[-0.01em] font-semibold text-[#1E1B17] text-[22px] leading-7 truncate">
          {deal.name}
        </div>
      </div>
      <div className="font-semibold text-[#1E1B17] text-[22px] leading-7 shrink-0">
        {deal.value ?? "—"}
      </div>
    </div>
  )
}

function StageStrip({ deal }: { deal: DealDrawerData }) {
  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex gap-[3px]" data-testid="drawer-stage-bars">
        {deal.pipeline.stages.map((stage) => (
          <div
            key={stage.id}
            className={`grow h-1 rounded-xs ${pipelineSegmentColor(stage, deal.status)}`}
          />
        ))}
      </div>
      <div className="flex gap-[3px]">
        {deal.pipeline.stages.map((stage) => {
          const baseClass =
            "grow tracking-[0.02em] uppercase text-[9px] leading-3"
          const weight = stage.isCurrent ? "font-semibold" : ""
          const colorClass = pipelineLabelColor(stage, deal.status)
          return (
            <div
              key={`${stage.id}-label`}
              className={`${baseClass} ${weight} ${colorClass}`}
            >
              {stage.name}
            </div>
          )
        })}
      </div>
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="tracking-[0.06em] uppercase font-semibold text-[#7A746B] text-[11px] leading-3">
      {children}
    </div>
  )
}
